import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { Helmet } from "react-helmet";
import { FormattedMessage, useIntl } from "react-intl";
import moment from "moment";
import Logo from "icons/new-logo.svg";
import NotFound from "icons/notFound.svg";
import sh from "bundles/common/utils/sh";
import { getFirstPathParam } from "bundles/common/utils/utils";
import ReadingProgressBar from "bundles/common/components/blog/ReadingProgressBar";
import TableOfContents from "bundles/common/components/blog/TableOfContents";
import BlogHero from "bundles/common/components/blog/BlogHero";
import BlogContent from "bundles/common/components/blog/BlogContent";
import BlogArticleLead from "bundles/common/components/blog/BlogArticleLead";
import BlogArticleSection from "bundles/common/components/blog/BlogArticleSection";
import RelatedArticles from "bundles/common/components/blog/RelatedArticles";
import FourBlogJobOffers from "bundles/Candidate/components/FourBlogJobOffers";

const toSlug = (value = "") =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Parses backend HTML into a lead block (content before first <h2>) and an
// array of sections (each <h2> title + the nodes that follow it). Section ids
// are slugified for TOC anchor links and scroll-spy.
const parseArticleContent = (content = "") => {
  if (!content) {
    return {
      leadHtml: "",
      sections: [],
    };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="root-content">${content}</div>`, "text/html");
  const root = doc.getElementById("root-content");

  if (!root) {
    return {
      leadHtml: content,
      sections: [],
    };
  }

  const nodes = Array.from(root.childNodes);
  const sections = [];
  const leadNodes = [];
  let currentSection = null;

  nodes.forEach((node) => {
    const isHeading = node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === "h2";

    if (isHeading) {
      const title = (node.textContent || "").trim();
      const id = `section-${sections.length}-${toSlug(title) || sections.length}`;
      currentSection = {
        id,
        title,
        contentParts: [],
      };
      sections.push(currentSection);
      return;
    }

    const serialized = node.outerHTML || node.textContent || "";

    if (currentSection) {
      currentSection.contentParts.push(serialized);
    } else {
      leadNodes.push(serialized);
    }
  });

  return {
    leadHtml: leadNodes.join(""),
    sections: sections.map((section) => ({
      id: section.id,
      title: section.title,
      html: section.contentParts.join(""),
    })),
  };
};

const BlogView = ({ type = "candidate" }) => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [similarArticles, setSimilarArticles] = useState([]);
  const intl = useIntl();
  const articleRef = useRef(null);
  const params = useParams();

  const publicArticleUrl = `${window.location.origin}/${i18nLocale}/${type}s/posts/${params?.id}`;

  const fetchArticle = () => {
    sh.get(`/blog_post/${params.id}`, {
      authenticity_token: authenticityToken,
    })
      .then((res) => {
        if (res?.data) {
          setArticle(res?.data);
        }
      })
      .catch((err) => {
        console.error(err);
        setNotFound(true);
      });
  };

  const fetchFourSimilarArticles = () => {
    sh.get("four_similar_articles", {
      params: {
        quantity: 4,
        authenticity_token: authenticityToken,
      },
    })
      .then((res) => {
        setSimilarArticles(res.data?.data || []);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  useEffect(() => {
    fetchArticle();
    fetchFourSimilarArticles();
  }, []);

  const { leadHtml, sections } = useMemo(() => parseArticleContent(article?.content || ""), [article?.content]);

  const allArticlesUrl = `/${i18nLocale}/${getFirstPathParam()}/posts`;
  const relatedArticles = useMemo(
    () =>
      similarArticles
        .filter((post) => post?.id && post.id !== article?.id)
        .slice(0, 3)
        .map((post) => ({
          ...post,
          href: `/${i18nLocale}/${getFirstPathParam()}/posts/${post.encoded_title}/${post.id}`,
        })),
    [similarArticles, article?.id, i18nLocale]
  );

  const dateLabel = article?.updated_at
    ? `${intl.formatMessage({ id: "blog.updated_at" })} ${moment(article.updated_at).format("DD.MM.YY")}`
    : "";

  const articleAuthor = "Squarehub";
  const articleImage = article?.cover_picture_url || NotFound;
  const authorLogo = `${window.location.origin}${Logo}`;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
      <Helmet>
        {article?.id && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "NewsArticle",
              headline: article.title,
              image: [articleImage],
              datePublished: article.created_at,
              author: (typeof article?.authors === "string" ? JSON.parse(article?.authors) : article.authors || []).map(
                (author) => ({
                  "@type": author.type,
                  name: author.name,
                  url: author.url,
                })
              ),
            })}
          </script>
        )}
      </Helmet>

      <ReadingProgressBar targetRef={articleRef} />
      <TableOfContents sections={sections.map((section) => ({ id: section.id, title: section.title }))} />

      {article?.id && (
        <>
          <Box ref={articleRef}>
            <BlogHero
              title={article.title}
              imageUrl={articleImage}
              dateLabel={dateLabel}
              author={articleAuthor}
              authorLogo={authorLogo}
              shareUrl={publicArticleUrl}
            />

            <Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />

            <BlogContent>
              <BlogArticleLead html={leadHtml} />
              {sections.map((section) => (
                <BlogArticleSection key={section.id} id={section.id} title={section.title} html={section.html} />
              ))}
            </BlogContent>
          </Box>

          <RelatedArticles articles={relatedArticles} allArticlesUrl={allArticlesUrl} />
        </>
      )}

      {notFound && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <Box component="img" src={NotFound} alt="Not Found" sx={{ width: 180, maxWidth: "60vw" }} />
        </Box>
      )}

      {type === "candidate" && (
        <Box sx={{ mb: 6 }}>
          <FourBlogJobOffers />
        </Box>
      )}
    </Box>
  );
};

export default BlogView;
